import { useEffect, useState } from "react";
import { globalAny, toReversed } from "../../utils/Utils";
import ButtonToggle from "../ButtonToggle";
import TouchableButton from "../TouchableButton";
import ComponentThemeType from "../../models/ComponentTheme";
import ComponentTypeEnum from "../../models/ComponentTypeEnum";
import useAcceptDataStore from "../../store/useAcceptData.store";
import CreateAccountButton from "../CreateAccountButton";
import PrivacyPolicyButton from "../PrivacyPolicyButton";
import * as CacheStorageUtils from "../../utils/CacheStorageUtils";
import { AsyncStorageKeys, Type } from "../../Types";
import { UserOptionSettings } from "../../enums/UserOptionSettings";
import UserOptionProps from "../../models/UserOptionProps";
import { updateUserOptions } from "../../services/accountService";
import { Props } from "./model";
import {
	StyledButtonContainer,
	StyledLegalTitleStyle,
	StyledNarrowBodyContent,
	StyledNarrowButton,
	StyledNarrowContent,
	StyledNarrowCreateButton,
	StyledNarrowMainContent,
	StyledNarrowTextContent,
	StyledWideBodyContent,
	StyledWideContent,
	StyledWideCreateButton,
	StyledWideFooter,
	StyledWideTextContent,
} from "./styled";
import useToggleStore from "../../store/useToggle.store";
import useGenericContentStore from "../../store/genericContent.store";
import WideCreateAccountWrapper from "../Layout/WideCreateAccountWrapper";
import NarrowCreateAccountWrapper from "../Layout/NarrowCreateAccountWrapper";
import { TouchableButtonDataProps } from "../../models/TouchableButtonDataProps";
import { CreateAccountButtonProps } from "../../models/CreateAccountButtonProps ";

const AcceptDataComponent = (props: Props) => {
	const { acceptData, setAcceptData } = useAcceptDataStore();
	const [optionDescription, setOptionDescription] = useState("");
	const isToggleHideDataMgmt = useToggleStore((state: any) => state.isToggleHideDataMgmt);
	const { isNarrow } = useGenericContentStore((state) => ({ isNarrow: state.isNarrow }));

	const createAccountButtonData: CreateAccountButtonProps[] = [
		{
			title: globalAny.language.sony_pictures,
			onPress: props.onPressSonyPictures,
			disabled: isToggleHideDataMgmt,
		},
		{ title: globalAny.language.sony_group, onPress: props.onPressSonyCompany, disabled: isToggleHideDataMgmt },
	];

	const touchableButtonData: TouchableButtonDataProps[] = [
		{
			title: globalAny.language.back,
			type: ComponentTypeEnum.Secondary,
			disabled: false,
			leftText: isNarrow,
			onPress: props.onPressSecondaryButton,
		},
		{
			title: globalAny.language.next,
			type: ComponentTypeEnum.Primary,
			disabled: false,
			leftText: isNarrow,
			onPress: props.onPressPrimaryButton,
		},
	];

	/**
	 * Fetches cached accept data depending on the component type
	 * and updates `optionDescription`.
	 */
	const asyncAcceptData = async () => {
		if (props.type === Type.Settings) {
			const acceptData = await CacheStorageUtils.getCacheItem(AsyncStorageKeys.acceptOtherDataSettings, true);
			const [sections] = acceptData?.sections ?? [];
			const [options] = sections?.options ?? [];
			const optionDesc = options?.optionsWordings?.find((val: any) => val?.key === "option-description");
			return setOptionDescription(optionDesc?.textValue);
		}

		if (props.type === Type.CreateAccount) {
			const acceptData = await CacheStorageUtils.getCacheItem(AsyncStorageKeys.acceptOtherDataSignUp, true);
			const [sections] = acceptData?.sections ?? [];
			const [options] = sections?.options ?? [];
			const optionDesc = options?.optionsWordings?.find((val: any) => val?.key === "option-description");
			return setOptionDescription(optionDesc?.textValue);
		}
	};

	/**
	 * Handles updating the user's "Accept Data" setting.
	 *
	 * Creates a `UserOptionProps` object with:
	 * - `optionId`: UserOptionSettings.AcceptData (constant enum value)
	 * - `isEnabled`: current `acceptData` state
	 * - `optionResponseId`: defaults to 0 for new updates
	 *
	 * Sends the update via `updateUserOptions` API call.
	 */
	const onUserOptions = async () => {
		const accept: UserOptionProps = {
			optionId: UserOptionSettings.AcceptData,
			isEnabled: acceptData,
			optionResponseId: 0,
		};

		await updateUserOptions(accept);
	};

	/**
	 * Renders a `CreateAccountButton` component with the given props.
	 *
	 * @param {Object} props - Props for the button.
	 * @param {string} props.title - The button text.
	 * @param {() => void} props.onPress - Callback when the button is pressed.
	 * @param {boolean} props.disabled - Whether the button is disabled.
	 *
	 * @returns JSX.Element - The rendered `CreateAccountButton`.
	 */
	const renderCreateAccountButton = ({ title, onPress, disabled }: CreateAccountButtonProps) => (
		<CreateAccountButton key={`create-account-button-${title}`} title={title} onPress={onPress} disabled={disabled} />
	);

	const renderTouchableButtonData = ({ title, type, leftText, disabled, onPress }: TouchableButtonDataProps) => (
		<TouchableButton
			key={`create-account-touchable-${title}`}
			componentTheme={ComponentThemeType.VinFast}
			type={type}
			onPress={onPress}
			disabled={disabled}
			leftText={leftText}
		>
			{title}
		</TouchableButton>
	);

	/**
	 * Renders the wide-screen layout for the Accept Data component.
	 *
	 * Includes:
	 * - A `ButtonToggle` to enable/disable accepting data.
	 * - Legal title and description text.
	 * - Create account buttons (e.g., Sony Pictures, Sony Group).
	 * - Footer buttons for navigation (Back / Next) and optional Privacy Policy link.
	 *
	 * @returns JSX.Element - The rendered wide-screen layout.
	 */
	const renderWideScreen = () => (
		<WideCreateAccountWrapper>
			<StyledWideContent disabled={isToggleHideDataMgmt}>
				<ButtonToggle defaultValue={acceptData} onPress={(state) => setAcceptData(state)} toggleDisabled={isToggleHideDataMgmt} />
				<StyledWideTextContent>
					<StyledLegalTitleStyle isNarrow={isNarrow}>{props.title ?? globalAny.language.accept_data}</StyledLegalTitleStyle>
					<StyledWideBodyContent>{props.description ?? optionDescription}</StyledWideBodyContent>
					<StyledWideCreateButton>
						{createAccountButtonData.map((item) => {
							return renderCreateAccountButton(item);
						})}
					</StyledWideCreateButton>
				</StyledWideTextContent>
			</StyledWideContent>

			<StyledWideFooter>
				<StyledButtonContainer>
					{touchableButtonData.map((item) => {
						return renderTouchableButtonData(item);
					})}
				</StyledButtonContainer>
				{props.showPrivacyLink && <PrivacyPolicyButton onPress={props.onPressPrivacy} />}
			</StyledWideFooter>
		</WideCreateAccountWrapper>
	);

	/**
	 * Renders the narrow-screen layout for the Accept Data component.
	 *
	 * Layout includes:
	 * - Footer buttons (Back / Next) displayed at the top for narrow screens.
	 * - Optional Privacy Policy link.
	 * - A `ButtonToggle` to enable/disable accepting data.
	 * - Legal title and description text.
	 * - Create account buttons (e.g., Sony Pictures, Sony Group).
	 *
	 * @returns JSX.Element - The rendered narrow-screen layout.
	 */
	const renderNarrowScreen = () => (
		<NarrowCreateAccountWrapper>
			<StyledNarrowButton>
				<StyledNarrowCreateButton>
					{toReversed(touchableButtonData).map((item) => {
						return renderTouchableButtonData(item);
					})}
				</StyledNarrowCreateButton>
				{props.showPrivacyLink && <PrivacyPolicyButton onPress={props.onPressPrivacy} />}
			</StyledNarrowButton>

			<StyledNarrowMainContent>
				<StyledNarrowContent disabled={isToggleHideDataMgmt}>
					<ButtonToggle defaultValue={acceptData} onPress={(state) => setAcceptData(state)} toggleDisabled={isToggleHideDataMgmt} />
					<StyledNarrowTextContent>
						<StyledLegalTitleStyle isNarrow={isNarrow}>{props.title ?? globalAny.language.accept_data}</StyledLegalTitleStyle>
						<StyledNarrowBodyContent>{props.description ?? optionDescription}</StyledNarrowBodyContent>
						<StyledNarrowCreateButton>
							{createAccountButtonData.map((item) => {
								return renderCreateAccountButton(item);
							})}
						</StyledNarrowCreateButton>
					</StyledNarrowTextContent>
				</StyledNarrowContent>
			</StyledNarrowMainContent>
		</NarrowCreateAccountWrapper>
	);

	/**
	 * Initializes the accept data state when the component mounts.
	 *
	 * If there is already saved accept data in the store, it sets it to ensure
	 * the toggle and related UI reflect the correct initial state.
	 */
	useEffect(() => {
		const savedAcceptData = acceptData;

		if (savedAcceptData) {
			setAcceptData(savedAcceptData);
		}
	}, []);

	/**
	 * Updates user options whenever the accept data state changes.
	 *
	 * - Sets the current accept data state in the store.
	 * - If the component is in settings mode, updates the backend via `onUserOptions`.
	 *
	 * @dependency [acceptData] - Triggered whenever `acceptData` changes.
	 */
	useEffect(() => {
		setAcceptData(acceptData);

		if (props.isSettings) {
			onUserOptions();
		}
	}, [acceptData]);

	/**
	 * Fetches and sets the option description when the component mounts.
	 *
	 * Calls `asyncAcceptData` to retrieve accept data details from cache or API
	 * depending on the component type (Settings or CreateAccount) and sets
	 * the description state for display.
	 */
	useEffect(() => {
		asyncAcceptData();
	}, []);

	return isNarrow ? renderNarrowScreen() : renderWideScreen();
};

export default AcceptDataComponent;
